import type { UserRole } from "./RoleContext";

/**
 * Role-Based Financial Visibility System
 * 
 * Controls what financial data each role can see across all modules.
 * Execution roles (crew/subs) see only their payout info.
 * Internal leadership sees full financials.
 */

export interface FinancialPermissions {
  viewContractValue: boolean;
  viewInternalCost: boolean;
  viewMargin: boolean;
  viewCrewPayout: boolean;
  viewFinancialSummary: boolean;
  viewCustomerPaymentStatus: boolean;
  viewChangeOrderValue: boolean;
  viewCompanyRevenue: boolean;
  viewPricingLogic: boolean;
  viewCredits: boolean;
  /** Internal warehouse staging, receiving, allocation panels */
  viewWarehouseInternals: boolean;
  /** Contractor dispatch recommendations, bids, response tables */
  viewDispatchInternals: boolean;
  /** Partner distributions / FIE earnings views */
  viewPartnerDistributions: boolean;
  /** Cross-rep performance leaderboards (rep tables, scorecards) */
  viewRepLeaderboard: boolean;
  /** Ops-only internal notes on records */
  viewInternalNotes: boolean;
  /** Whether this role's data view is scoped to "own" records (e.g. own jobs only) */
  scopedToOwn: boolean;
}

/** Helper: build full permission record from base flags + section overrides */
type BaseFlags = Omit<FinancialPermissions, "viewWarehouseInternals" | "viewDispatchInternals" | "viewPartnerDistributions" | "viewRepLeaderboard" | "viewInternalNotes" | "scopedToOwn">;
function build(base: BaseFlags, sections: Partial<Pick<FinancialPermissions, "viewWarehouseInternals" | "viewDispatchInternals" | "viewPartnerDistributions" | "viewRepLeaderboard" | "viewInternalNotes" | "scopedToOwn">> = {}): FinancialPermissions {
  return {
    ...base,
    viewWarehouseInternals: sections.viewWarehouseInternals ?? false,
    viewDispatchInternals: sections.viewDispatchInternals ?? false,
    viewPartnerDistributions: sections.viewPartnerDistributions ?? false,
    viewRepLeaderboard: sections.viewRepLeaderboard ?? false,
    viewInternalNotes: sections.viewInternalNotes ?? false,
    scopedToOwn: sections.scopedToOwn ?? false,
  };
}

const FULL_FIN: BaseFlags = {
  viewContractValue: true, viewInternalCost: true, viewMargin: true,
  viewCrewPayout: true, viewFinancialSummary: true, viewCustomerPaymentStatus: true,
  viewChangeOrderValue: true, viewCompanyRevenue: true, viewPricingLogic: true, viewCredits: true,
};
const ALL_SECTIONS = {
  viewWarehouseInternals: true, viewDispatchInternals: true,
  viewPartnerDistributions: true, viewRepLeaderboard: true, viewInternalNotes: true,
};

/** Default permission sets by role */
const ROLE_FINANCIAL_PERMISSIONS: Record<UserRole, FinancialPermissions> = {
  super_admin: build(FULL_FIN, ALL_SECTIONS),
  owner: build(FULL_FIN, ALL_SECTIONS),
  cfo: build(FULL_FIN, { ...ALL_SECTIONS }),
  finance_manager: build({ ...FULL_FIN, viewPricingLogic: false }, { viewPartnerDistributions: true, viewRepLeaderboard: true, viewInternalNotes: true }),
  bookkeeper: build({ ...FULL_FIN, viewMargin: false, viewCompanyRevenue: false, viewPricingLogic: false }, { viewInternalNotes: true }),
  ops_manager: build({
    viewContractValue: true, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: true, viewFinancialSummary: true, viewCustomerPaymentStatus: true,
    viewChangeOrderValue: true, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: true,
  }, { viewWarehouseInternals: true, viewDispatchInternals: true, viewInternalNotes: true, viewRepLeaderboard: true }),
  admin: build({
    viewContractValue: true, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: true, viewFinancialSummary: true, viewCustomerPaymentStatus: true,
    viewChangeOrderValue: true, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: true,
  }, { viewWarehouseInternals: true, viewDispatchInternals: true, viewInternalNotes: true, viewRepLeaderboard: true }),
  it_staff: build({
    viewContractValue: true, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: true, viewFinancialSummary: true, viewCustomerPaymentStatus: true,
    viewChangeOrderValue: true, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: true,
  }, { viewWarehouseInternals: true, viewDispatchInternals: true, viewInternalNotes: true, viewRepLeaderboard: true }),
  head_of_installation: build({
    viewContractValue: false, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: true, viewFinancialSummary: false, viewCustomerPaymentStatus: false,
    viewChangeOrderValue: false, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: false,
  }, { viewWarehouseInternals: true, viewDispatchInternals: true, viewInternalNotes: true }),
  procurement_manager: build({
    viewContractValue: false, viewInternalCost: true, viewMargin: false,
    viewCrewPayout: false, viewFinancialSummary: false, viewCustomerPaymentStatus: false,
    viewChangeOrderValue: false, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: false,
  }, { viewWarehouseInternals: true, viewInternalNotes: true }),
  inventory_manager: build({
    viewContractValue: false, viewInternalCost: true, viewMargin: false,
    viewCrewPayout: false, viewFinancialSummary: false, viewCustomerPaymentStatus: false,
    viewChangeOrderValue: false, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: false,
  }, { viewWarehouseInternals: true, viewInternalNotes: true }),
  warehouse_manager: build({
    viewContractValue: false, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: false, viewFinancialSummary: false, viewCustomerPaymentStatus: false,
    viewChangeOrderValue: false, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: false,
  }, { viewWarehouseInternals: true, viewInternalNotes: true }),
  // Sales Rep — own data only, no internal margin/cost, no cross-rep, no ops internals
  sales: build({
    viewContractValue: true, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: false, viewFinancialSummary: true, viewCustomerPaymentStatus: true,
    viewChangeOrderValue: true, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: true,
  }, { scopedToOwn: true }),
  sales_manager: build({
    viewContractValue: true, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: false, viewFinancialSummary: true, viewCustomerPaymentStatus: true,
    viewChangeOrderValue: true, viewCompanyRevenue: true, viewPricingLogic: false, viewCredits: true,
  }, { viewRepLeaderboard: true, viewInternalNotes: true }),
  call_center: build({
    viewContractValue: false, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: false, viewFinancialSummary: false, viewCustomerPaymentStatus: false,
    viewChangeOrderValue: false, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: false,
  }),
  installer: build({
    viewContractValue: false, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: true, viewFinancialSummary: false, viewCustomerPaymentStatus: false,
    viewChangeOrderValue: false, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: false,
  }),
  subcontractor: build({
    viewContractValue: false, viewInternalCost: false, viewMargin: false,
    viewCrewPayout: true, viewFinancialSummary: false, viewCustomerPaymentStatus: false,
    viewChangeOrderValue: false, viewCompanyRevenue: false, viewPricingLogic: false, viewCredits: false,
  }),
};

export function getFinancialPermissions(role: UserRole): FinancialPermissions {
  return ROLE_FINANCIAL_PERMISSIONS[role];
}

/** Whether the role is a crew/execution role that sees only payout info */
export function isCrewRole(role: UserRole): boolean {
  return role === "installer" || role === "subcontractor";
}

/** Whether the role has full financial access */
export function hasFullFinancialAccess(role: UserRole): boolean {
  return ["super_admin", "owner", "cfo", "finance_manager"].includes(role);
}

/**
 * Crew-facing label translations.
 * Converts internal financial terms to execution-friendly language.
 */
export const CREW_FINANCIAL_LABELS: Record<string, string> = {
  "Contract Value": "Install Payout",
  "Original Contract": "Approved Payout",
  "Current Contract": "Current Payout",
  "Net Value": "Earned Amount",
  "Financial Summary": "Compensation",
  "Change Orders": "Scope Adjustments",
  "Credits Issued": "Adjustments",
  "Material Cost": "Materials",
  "Labor Cost": "Labor",
  "Est. Margin": "—",
  "Sub Payout": "Your Payout",
  "Revenue": "Compensation",
  "Margin": "—",
  "Cost": "—",
  "Profit": "—",
  "Markup": "—",
};

/** Get crew-friendly label or original if not crew role */
export function getFinancialLabel(label: string, role: UserRole): string {
  if (!isCrewRole(role)) return label;
  return CREW_FINANCIAL_LABELS[label] ?? label;
}

/** Permission key descriptions for settings UI */
export const PERMISSION_DESCRIPTIONS: Record<keyof FinancialPermissions, string> = {
  viewContractValue: "View contract and agreement values",
  viewInternalCost: "View material, labor, and internal costs",
  viewMargin: "View profit margins and margin percentages",
  viewCrewPayout: "View crew/subcontractor payout amounts",
  viewFinancialSummary: "View job financial summary cards",
  viewCustomerPaymentStatus: "View customer payment and deposit status",
  viewChangeOrderValue: "View change order dollar amounts",
  viewCompanyRevenue: "View company-wide revenue and pipeline data",
  viewPricingLogic: "View pricing formulas and guardrails",
  viewCredits: "View issued customer credits and resolutions",
  viewWarehouseInternals: "View warehouse staging, receiving, allocation panels",
  viewDispatchInternals: "View contractor dispatch recommendations and bid responses",
  viewPartnerDistributions: "View partner distributions and earnings forecasts",
  viewRepLeaderboard: "View cross-rep performance leaderboards",
  viewInternalNotes: "View ops-only internal notes",
  scopedToOwn: "Data is scoped to records owned by this user",
};
